import React from "react"
import { Check, X } from "lucide-react"
import { cn } from "@/lib/utils"

interface PasswordValidationProps {
  password: string
  passwordConfirmation: string
}

const CATEGORY_CHECKS = [
  { label: "Uppercase letter", test: (pw: string) => /[A-Z]/.test(pw) },
  { label: "Lowercase letter", test: (pw: string) => /[a-z]/.test(pw) },
  { label: "Number", test: (pw: string) => /\d/.test(pw) },
  { label: "Special character", test: (pw: string) => /[^A-Za-z0-9]/.test(pw) },
]

// Must match app/models/concerns/password_complexity_validator.rb
function getValidationState(password: string, passwordConfirmation: string) {
  const hasMinLength = password.length >= 8
  const categoryResults = CATEGORY_CHECKS.map((c) => c.test(password))
  const categoriesMet = categoryResults.filter(Boolean).length
  const hasEnoughCategories = categoriesMet >= 3
  const confirmationMatches =
    passwordConfirmation.length > 0 && password === passwordConfirmation
  const isValid = hasMinLength && hasEnoughCategories && confirmationMatches
  return { hasMinLength, categoryResults, categoriesMet, hasEnoughCategories, confirmationMatches, isValid }
}

export function validatePassword(password: string, passwordConfirmation: string) {
  return getValidationState(password, passwordConfirmation).isValid
}

export function PasswordValidation({ password, passwordConfirmation }: PasswordValidationProps) {
  if (password.length === 0 && passwordConfirmation.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        Password must be at least 8 characters and include at least 3 of:
        uppercase, lowercase, number, special character.
      </p>
    )
  }

  const { hasMinLength, categoryResults, categoriesMet, hasEnoughCategories, confirmationMatches } =
    getValidationState(password, passwordConfirmation)

  return (
    <div className="space-y-2 text-xs">
      <Rule met={hasMinLength}>At least 8 characters</Rule>
      <div>
        <Rule met={hasEnoughCategories}>
          At least 3 of the following ({categoriesMet}/3):
        </Rule>
        <div className="ml-5 mt-1 space-y-1">
          {CATEGORY_CHECKS.map((check, i) => (
            <Rule key={check.label} met={categoryResults[i]}>
              {check.label}
            </Rule>
          ))}
        </div>
      </div>
      {passwordConfirmation.length > 0 && (
        <Rule met={confirmationMatches}>Passwords match</Rule>
      )}
    </div>
  )
}

function Rule({ met, children }: { met: boolean; children: React.ReactNode }) {
  return (
    <div className={cn("flex items-center gap-1.5", met ? "text-green-600" : "text-muted-foreground")}>
      {met ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      <span>{children}</span>
    </div>
  )
}
